import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Communicator, { isApiInitiator } from 'models/communicator';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import { CustomAttributeUpdateMessage, Operation } from './custom_attribute_change_item';
import i18n from 'scripts/i18n/i18n';
import ItemContent from '../item_content';
import Metadata from '../metadata/metadata';
import PrepositionalPhrase from '../metadata/prepositional_phrase';
import { StyledIconAPI } from '../metadata/api_metadata';
import { TaskLink } from '../task/task_routing_item';

// TaskCustomAttributeChangeItem renders the customer-timeline audit line for a
// custom-attribute change on a task (backend content type
// TASK_CUSTOM_ATTRIBUTE_CHANGE). Reuses the conversation change message but
// appends "to <Task>" where Task links to the task the change was applied to.
export function TaskCustomAttributeChangeItem({ added, currentAgentId, initiatorName, item, removed, taskId }) {
  const { t } = useTranslation();
  const icon = isApiInitiator(item.initiator) ? <StyledIconAPI className="api-icon">APP</StyledIconAPI> : null;
  const initiator = { id: item.initiator?.id, type: item.initiator?.type, name: initiatorName };

  const taskPhrase = (
    <PrepositionalPhrase
      complement={<TaskLink className="itemMetadata-subject" taskItemId={taskId} />}
      preposition={t('to')}
      suppressHighlight
    />
  );

  return (
    <>
      {added.length > 0 ? (
        <div data-aid="taskCustomAttributeAdded">
          <ItemContent isAuditItem item={item}>
            <Metadata
              icon={icon}
              prepositionalPhrases={taskPhrase}
              text={
                <CustomAttributeUpdateMessage
                  currentAgentId={currentAgentId}
                  initiator={initiator}
                  operation={Operation.ADDED}
                  values={added}
                />
              }
              timestamp={item.timestamp}
            />
          </ItemContent>
        </div>
      ) : null}
      {removed.length > 0 ? (
        <div data-aid="taskCustomAttributeRemoved">
          <ItemContent isAuditItem item={item}>
            <Metadata
              icon={icon}
              prepositionalPhrases={taskPhrase}
              text={
                <CustomAttributeUpdateMessage
                  currentAgentId={currentAgentId}
                  initiator={initiator}
                  operation={Operation.REMOVED}
                  values={removed}
                />
              }
              timestamp={item.timestamp}
            />
          </ItemContent>
        </div>
      ) : null}
    </>
  );
}

TaskCustomAttributeChangeItem.propTypes = {
  added: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentAgentId: PropTypes.string,
  initiatorName: PropTypes.string,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  removed: PropTypes.arrayOf(PropTypes.string).isRequired,
  taskId: PropTypes.string,
};

function mapStateToProps({ getProvider }, { item }) {
  const getAttributeNames = attributes =>
    attributes
      .map(attr => {
        const customAttribute = getProvider('customAttributes').findBy({ id: attr.id });
        return customAttribute
          ? `${customAttribute.label} > ${attr.value}${customAttribute.deactivated ? ` (${i18n.t('Archived')})` : ''}`
          : null;
      })
      .filter(Boolean);

  function getInitiatorName(initiator) {
    return (initiator?.type === Communicator.AGENT && getProvider('agents').findBy({ id: initiator.id })?.name) || null;
  }

  return {
    added: _.sortBy(getAttributeNames(item.content.added), attr => attr.toLowerCase()),
    currentAgentId: getProvider('currentAgent').get().id,
    initiatorName: getInitiatorName(item.initiator),
    item,
    removed: _.sortBy(getAttributeNames(item.content.removed), attr => attr.toLowerCase()),
    taskId: item.content.taskId,
  };
}

const TaskCustomAttributeChangeItemContainer = connect(mapStateToProps)(TaskCustomAttributeChangeItem);
TaskCustomAttributeChangeItemContainer.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

export default TaskCustomAttributeChangeItemContainer;
